import {
  ArrowRight,
  ExternalLink,
  FlaskConical,
  GraduationCap,
  Sparkles,
  Zap,
  Users,
  Globe,
  Swords,
  Code2,
  Mail,
  RadioTower,
  Truck,
  Landmark,
  Hammer,
} from 'lucide-react';
import { TrinetraLoop3 } from '@nirvikailabs/brand/icons';

/**
 * nirvik.ai — flagship company landing.
 *
 * Tone: mission-driven, founder-voice, high-gravitas. Positions Nirvik AI
 * as the parent company for three surfaces: the Trinetra product, the
 * Learning platform, and the Labs research initiative.
 *
 * Visual identity: dark-mode-first (the <html> ships with `class="dark"`),
 * indigo surface with warm turmeric accents + saffron/india-green
 * tricolor callouts. Ambient halos (bg-halo-turmeric, bg-halo-indigo,
 * bg-halo-green) replace the old labs-page blue glows so the brand palette
 * stays coherent across every Nirvik surface.
 */
export function LandingPage() {
  return (
    <div className="min-h-screen bg-background text-foreground overflow-hidden">
      {/* ─── Ambient halos ─── fixed so they parallax with scroll. Three
       * widely-spaced halos in the brand palette — turmeric top-centre for
       * warmth on hero, chakra-navy bottom-right for depth, india-green
       * mid-left to seed the "growth" association for the Learning
       * product card later on the page. */}
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className="absolute -top-48 left-1/2 -translate-x-1/2 w-[1100px] h-[650px] bg-halo-turmeric" />
        <div className="absolute -bottom-20 -right-20 w-[600px] h-[500px] bg-halo-indigo" />
        <div className="absolute top-1/2 -left-24 w-[450px] h-[450px] bg-halo-green opacity-70" />
      </div>

      {/* ─── Nav ───
       * Sticky-less on purpose — marketing pages feel lighter without a
       * hovering chrome bar. Responsive collapses: on phones we keep only
       * the brand + primary CTA; mid-level links appear from `md:` up. */}
      <nav className="relative z-10 flex items-center justify-between px-5 sm:px-8 py-5 max-w-6xl mx-auto gap-4">
        <a href="/" className="flex items-center gap-2.5 shrink-0">
          <TrinetraLoop3 className="h-7 w-7 text-primary-strong" />
          <span className="font-display text-lg sm:text-xl font-semibold tracking-wide">Nirvik AI Labs</span>
        </a>
        <div className="hidden md:flex items-center gap-7 text-sm text-muted-foreground">
          <a href="https://trinetra.nirvik.ai" className="hover:text-foreground transition-colors">Trinetra</a>
          <a href="https://learning.nirvik.ai" className="hover:text-foreground transition-colors">Learning</a>
          <a href="https://research.nirvik.ai" className="hover:text-foreground transition-colors">Research</a>
        </div>
        <a
          href="mailto:hello@nirvik.ai"
          className="text-sm font-medium text-primary-strong hover:text-foreground transition-colors inline-flex items-center gap-1.5 shrink-0"
        >
          Contact
          <ArrowRight className="h-3.5 w-3.5" />
        </a>
      </nav>

      {/* ─── Hero ───
       * Founding-day badge, oversized Trinetra mark with a halo-ring pulse,
       * gradient-text headline. The gradient uses brand colours
       * (foreground → turmeric → saffron) instead of the old labs blue. */}
      <section className="relative z-10 px-5 sm:px-6 pt-16 sm:pt-24 pb-24 sm:pb-32 max-w-4xl mx-auto text-center">
        <div className="inline-flex items-center gap-2 bg-primary/10 border border-primary/25 text-primary-strong px-4 py-1.5 rounded-full text-sm font-medium mb-10">
          <Sparkles className="h-4 w-4" />
          April 7, 2026 — Probably Just Another Day For Many, But a Historic One For Us. Nirvik AI Labs Is Born. <Sparkles className="h-4 w-4" />
        </div>

        {/* Hero mark — outer halo ring + the living radar icon */}
        <div className="flex justify-center mb-10">
          <div className="relative">
            <div className="h-24 w-24 rounded-[28px] bg-gradient-to-br from-primary/90 to-saffron/80 flex items-center justify-center shadow-[0_25px_60px_-15px_color-mix(in_oklch,var(--primary)_40%,transparent)]">
              <TrinetraLoop3 className="h-12 w-12 text-primary-foreground" />
            </div>
            <div className="absolute -inset-4 rounded-[32px] bg-primary/15 blur-2xl -z-10 trinetra-halo" />
          </div>
        </div>

        <h1 className="font-display text-5xl sm:text-7xl md:text-8xl font-semibold tracking-tight leading-[1.02] mb-6 bg-gradient-to-br from-foreground via-primary-strong to-saffron bg-clip-text text-transparent">
          Nirvik AI Labs
        </h1>

        <p className="text-lg sm:text-2xl text-foreground/90 font-medium tracking-wide mb-3">
          Building the Next Generation of Cyber Defenses
        </p>
        <p className="text-sm sm:text-base text-muted-foreground max-w-xl mx-auto">
          for every builder on the planet
        </p>

        <div className="h-px w-24 mx-auto mt-12 bg-gradient-to-r from-transparent via-primary-strong to-transparent" />
      </section>

      {/* ─── From the Founders ───
       * Personal narrative section. Establishes standing (25+ years in
       * mission-critical systems), shows we were already pointed at this
       * problem (AI transformation + cyber resilience), and lands on the
       * builder+hacker thesis that justifies why a NEW company is the
       * right answer. Bridges into the Inflection Point section which
       * explains what "April 7, 2026" actually was.
       *
       * Visual rhythm: narrative text (center-aligned prose) → 4-pillar
       * credentials grid → teaser callback → bold pull-quote thesis. The
       * pull-quote uses a left-border accent rather than a gradient card
       * so it reads distinct from the gradient "We choose to build
       * defenders." callout in the next section. */}
      <section className="relative z-10 px-5 sm:px-6 py-20 max-w-3xl mx-auto">
        <h2 className="text-xs font-bold uppercase tracking-[0.24em] text-primary-strong mb-6 text-center">
          From the Founders
        </h2>
        <p className="font-display text-3xl sm:text-4xl md:text-5xl font-semibold text-center text-foreground mb-10 leading-tight">
          Building mission critical systems for over 25 years
        </p>

        <div className="space-y-7 text-base sm:text-lg md:text-xl leading-relaxed text-foreground/90">
          <p>
            We've spent our careers building mission-critical software solutions —
            wireless telecom systems that process calls including emergency and first-responder traffic,
            supply chains that move goods across continents,
            federal platforms that support critical programs and serve millions of citizens,
            and a variety of bootcamps and training programs building the next generation of talent.
          </p>
        </div>

        <div className="space-y-7 text-base sm:text-lg md:text-xl leading-relaxed text-foreground/90 mt-12">
          <p>
            And for the last couple of years, we've been leading AI transformation across the organizations,
            modernizing how decisions get made, how software ships, how teams actually work.
            We've believed cyber resilience deserves the same reinvention.
            It's been on the roadmap. Always a few quarters out.
          </p>
          <p className="text-foreground font-medium italic">
            Then, on April 7, 2026, the future arrived early.
            At least, for some of us, the part of the future we were building towards.
            The inflection point we weren't expecting so soon.
          </p>
        </div>
      </section>

      {/* ─── The Inflection Point ───
       * What happened on April 7. The founder section above teased the
       * date; this one names the project and lays out the implications.
       * Carried over from the original labs launch page — remains the
       * emotional anchor of the narrative. */}
      <section className="relative z-10 px-5 sm:px-6 py-20 max-w-3xl mx-auto">
        <h2 className="text-xs font-bold uppercase tracking-[0.24em] text-primary-strong mb-8 text-center">
          The Inflection Point
        </h2>

        <div className="space-y-7 text-base sm:text-lg md:text-xl leading-relaxed text-foreground/90">
          <p>
            That day, Anthropic unveiled{' '}
            <a
              href="https://red.anthropic.com/2026/mythos-preview/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary-strong hover:text-foreground underline underline-offset-4 decoration-primary/40 inline-flex items-center gap-1"
            >
              Project GlassWing and Claude Mythos
              <ExternalLink className="h-4 w-4" />
            </a>
            {' '}— a frontier model that autonomously discovers zero-day vulnerabilities hidden for decades, builds multi-stage exploit chains, and breaches systems that withstood years of manual review and fuzzing.
          </p>
          <p>
            A 27-year-old vulnerability in OpenBSD. A 17-year-old remote root exploit in FreeBSD. Over 1,000 critical vulnerabilities surfaced across every major operating system and browser — in a single research cycle.
          </p>
          <p className="text-foreground font-semibold text-xl sm:text-2xl py-3">
            The era of AI-native cybersecurity is here. Not in two or five years. Today.
          </p>
          <p>
            The question is no longer <em>whether</em> AI will transform cybersecurity.
            The question is <em>who</em> will lead that transformation.
            Who will build the new defenses that can keep up with the new offense. And how.
          </p>
        </div>

        {/* Capstone thesis — the builder+hacker line. Left-border pull-
         * quote so it's visually distinct from the gradient callout in
         * the Inflection Point section. Uses Hammer icon as a subtle
         * anchor on the left edge. */}
        <div className="mt-12 pl-5 sm:pl-7 border-l-[3px] border-primary/70 relative">
          <div className="absolute -left-[14px] top-0 h-7 w-7 rounded-full bg-background border-[3px] border-primary/70 flex items-center justify-center">
            <Hammer className="h-3 w-3 text-primary-strong" />
          </div>
          <p className="text-xl sm:text-2xl md:text-3xl font-semibold text-foreground leading-snug">
            This moment calls for a <span className="text-primary-strong">new breed of builder + hacker mindset</span>,
            to prepare and build intelligent and autonomous cyber defenses the next generation will inherit.
          </p>
        </div>

        <div className="mt-14 space-y-7 text-base sm:text-lg md:text-xl leading-relaxed text-foreground/90">
          <p>
            For us, this felt like a calling, not a pivot.
            Building mission-critical systems teaches us to treat security as an engineering discipline:
            defense in depth, zero trust, shift left and continuous observability.
            We must bake it into the design, the first commit, the pipeline and the deployment.
          </p>
          <p className="text-foreground font-medium">
            That practitioner and builder vantage point is exactly what this moment demands.
            Agentic AI is now building and shipping software faster than any team can manually review.
            If the systems being built don't have security in their foundations —
            by design, not by audit, no perimeter tool closes that gap.
          </p>
          <p className="text-foreground font-medium">
            So here we are!  We set out to build a new kind of platform, an AI native cybersecurity platform
            that meets builders where they are, helps them build in defenses from the start,
            and keeps them ahead of the evolving threat landscape with autonomous, always-on protection.
            Individual builders, small teams, and large enterprises alike.
          </p>
        </div>

      </section>

      {/* ─── Divider ─── */}
      <div className="relative z-10 flex justify-center py-10">
        <div className="flex items-center gap-3">
          <div className="h-px w-16 bg-border" />
          <TrinetraLoop3 className="h-5 w-5 text-muted-foreground" />
          <div className="h-px w-16 bg-border" />
        </div>
      </div>

      {/* ─── The Name ───
       * Quiet etymology beat placed just before the product surfaces.
       * Acts as a soft brand reset after the founders' story and the
       * inflection-point narrative, before we introduce Trinetra /
       * Learning / Research. Small, centered, muted — reads as a quiet
       * aside, not a section header. */}
      <section className="relative z-10 px-5 sm:px-6 pt-2 pb-10 max-w-2xl mx-auto text-center">
        <p className="text-base sm:text-lg md:text-xl leading-relaxed text-muted-foreground mb-6">
          <span className="text-primary-strong font-medium">Nirvik</span>{' '}
          <span className="text-muted-foreground/80">— Sanskrit for</span>{' '}
          <span className="text-primary-strong font-medium">the bold and the fearless</span>.
        </p>
        <p className="text-base sm:text-lg md:text-xl leading-relaxed text-foreground/90">
          We chose it deliberately. Defending what matters in this new era demands that
          posture — bold enough to rebuild from the foundations, fearless enough to run
          toward the hardest problems.
        </p>
      </section>

      {/* ─── What We're Building ───
       * The three surfaces of Nirvik: Trinetra (product), Learning
       * (education), Labs (research). First two link off to their own
       * subdomains; Labs anchors down the page to our research pillars. */}
      <section id="platforms" className="relative z-10 px-5 sm:px-6 py-20 max-w-6xl mx-auto">
        <h2 className="text-xs font-bold uppercase tracking-[0.24em] text-primary-strong mb-4 text-center">
          What We're Building
        </h2>
        <p className="font-display text-3xl sm:text-4xl md:text-5xl font-semibold text-center text-foreground mb-4 leading-tight max-w-3xl mx-auto">
          Three surfaces, one mission
        </p>
        <p className="text-base sm:text-lg text-muted-foreground text-center max-w-2xl mx-auto mb-14">
          A platform that defends. A platform that prepares. A lab that reinvents.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 sm:gap-6">
          {/* Trinetra — flagship product */}
          <a
            href="https://trinetra.nirvik.ai"
            className="group relative p-7 sm:p-8 rounded-2xl bg-card border border-border hover:border-primary/40 transition-all"
          >
            <div className="absolute inset-0 rounded-2xl bg-primary/5 opacity-0 group-hover:opacity-100 transition-opacity -z-10 blur-xl" />
            <div className="flex items-center justify-between mb-6">
              <div className="h-12 w-12 rounded-xl bg-primary/15 border border-primary/25 flex items-center justify-center">
                <TrinetraLoop3 className="h-7 w-7 text-primary-strong" />
              </div>
              <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-primary-strong bg-primary/10 px-2.5 py-1 rounded-full border border-primary/25">
                Platform
              </span>
            </div>
            <h3 className="font-display text-xl font-semibold text-foreground mb-2">Trinetra</h3>
            <p className="text-sm text-muted-foreground leading-relaxed mb-5">
              The agentic AI platform with specialized agents that scan your code, infrastructure, and cloud
              and protect your systems and environments. Always-on, autonomous, and accessible to every builder.
            </p>
            <span className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-strong group-hover:gap-2 transition-all">
              Visit trinetra.nirvik.ai <ArrowRight className="h-3.5 w-3.5" />
            </span>
          </a>

          {/* Learning — education platform */}
          <a
            href="https://learning.nirvik.ai"
            className="group relative p-7 sm:p-8 rounded-2xl bg-card border border-border hover:border-india-green/50 transition-all"
          >
            <div className="absolute inset-0 rounded-2xl bg-india-green/5 opacity-0 group-hover:opacity-100 transition-opacity -z-10 blur-xl" />
            <div className="flex items-center justify-between mb-6">
              <div className="h-12 w-12 rounded-xl bg-india-green/15 border border-india-green/30 flex items-center justify-center">
                <GraduationCap className="h-7 w-7 text-india-green" />
              </div>
              <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-india-green bg-india-green/10 px-2.5 py-1 rounded-full border border-india-green/30">
                Education
              </span>
            </div>
            <h3 className="font-display text-xl font-semibold text-foreground mb-2">Learning</h3>
            <p className="text-sm text-muted-foreground leading-relaxed mb-5">
              Free, interactive, hands-on security concepts and practices for builders at every stage.
              Plain-language lessons, hands-on labs, and tracks for builders, professionals, and educators.
            </p>
            <span className="inline-flex items-center gap-1.5 text-sm font-medium text-india-green group-hover:gap-2 transition-all">
              Visit learning.nirvik.ai <ArrowRight className="h-3.5 w-3.5" />
            </span>
          </a>

          {/* Labs — research */}
          <a
            href="https://research.nirvik.ai"
            className="group relative p-7 sm:p-8 rounded-2xl bg-card border border-border hover:border-saffron/50 transition-all"
          >
            <div className="absolute inset-0 rounded-2xl bg-saffron/5 opacity-0 group-hover:opacity-100 transition-opacity -z-10 blur-xl" />
            <div className="flex items-center justify-between mb-6">
              <div className="h-12 w-12 rounded-xl bg-saffron/15 border border-saffron/30 flex items-center justify-center">
                <FlaskConical className="h-7 w-7 text-saffron" />
              </div>
              <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-saffron bg-saffron/10 px-2.5 py-1 rounded-full border border-saffron/30">
                Research
              </span>
            </div>
            <h3 className="font-display text-xl font-semibold text-foreground mb-2">Research</h3>
            <p className="text-sm text-muted-foreground leading-relaxed mb-5">
              Practical research on agentic and autonomous defenses,
              tools, new mindset and practices for the new breed of builders,
              and advancing the science of security and safety in the age of AI.
            </p>
            <span className="inline-flex items-center gap-1.5 text-sm font-medium text-saffron group-hover:gap-2 transition-all">
              Read our research <ArrowRight className="h-3.5 w-3.5" />
            </span>
          </a>
        </div>
      </section>

      {/* ─── Mission pillars ─── */}
      <section id="mission" className="relative z-10 px-5 sm:px-6 py-24 max-w-5xl mx-auto">
        <h2 className="text-xs font-bold uppercase tracking-[0.24em] text-primary-strong mb-4 text-center">
          Our Mission
        </h2>
        <p className="font-display text-2xl sm:text-3xl md:text-4xl font-semibold text-center text-foreground mb-4 max-w-3xl mx-auto leading-tight">
          Making advanced cybersecurity accessible to every builder on the planet
        </p>
        <p className="text-base text-muted-foreground text-center max-w-2xl mx-auto mb-14">
          The next billion lines of code will be written by builders who aren't security experts. We're making sure that doesn't matter.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 sm:gap-6">
          {[
            {
              icon: Zap,
              title: 'AI-Powered Defense',
              description: 'Harness frontier models to find and fix vulnerabilities before attackers do. Autonomous scanning, intelligent triage, plain-language remediation.',
              iconBg: 'bg-primary/15 border-primary/30 text-primary-strong',
            },
            {
              icon: Users,
              title: 'Democratize Security',
              description: 'Translate complex security concepts into language anyone can understand. Every vulnerability explained. Every fix actionable. No expertise required.',
              iconBg: 'bg-chakra-navy/15 border-chakra-navy/35 text-chakra-navy',
            },
            {
              icon: Globe,
              title: 'Open Research',
              description: 'Advance the science of automated vulnerability discovery and responsible disclosure. Publish findings. Share tools. Strengthen the ecosystem.',
              iconBg: 'bg-india-green/15 border-india-green/30 text-india-green',
            },
          ].map((pillar) => (
            <div
              key={pillar.title}
              className="p-7 rounded-2xl bg-card border border-border hover:border-border/70 transition-all hover:-translate-y-0.5"
            >
              <div className={`h-12 w-12 rounded-xl border flex items-center justify-center mb-5 ${pillar.iconBg}`}>
                <pillar.icon className="h-6 w-6" />
              </div>
              <h3 className="font-display text-lg font-semibold text-foreground mb-2">{pillar.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{pillar.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ─── Divider ─── */}
      <div className="relative z-10 flex justify-center py-10">
        <div className="flex items-center gap-3">
          <div className="h-px w-16 bg-border" />
          <TrinetraLoop3 className="h-5 w-5 text-muted-foreground" />
          <div className="h-px w-16 bg-border" />
        </div>
      </div>

      {/* ─── Quote / future ───
       * Anthropic quote lives here as both credibility signal AND thematic
       * anchor. Our "Today." callback echoes the hero. */}
      <section className="relative z-10 px-5 sm:px-6 py-24 max-w-3xl mx-auto text-center">
        <blockquote className="text-lg sm:text-xl md:text-2xl font-medium text-foreground/90 italic leading-relaxed">
          &ldquo;In the long run, we expect that defense capabilities will dominate&hellip; the world will emerge more secure, with software better hardened.&rdquo;
        </blockquote>
        <cite className="block mt-4 text-sm text-muted-foreground not-italic">
          — Anthropic, Project GlassWing
        </cite>

        <p className="font-display text-2xl sm:text-3xl md:text-4xl font-semibold text-foreground mt-12 leading-tight">
          We're building for that future.{' '}
          <span className="bg-gradient-to-r from-primary-strong to-saffron bg-clip-text text-transparent">
            Today.
          </span>
        </p>
      </section>

      <div className="h-px w-24 mx-auto mt-12 bg-gradient-to-r from-transparent via-primary-strong to-transparent" />


      {/* ─── CTA ─── */}
      <section className="relative z-10 px-5 sm:px-6 py-20 bg-gradient-to-b from-transparent via-chakra-navy/10 to-background">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="font-display text-3xl sm:text-4xl font-semibold text-foreground mb-4">
            Join the Mission
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground mb-10">
            Whether you're a security researcher, a builder, or someone who believes software
            should be safer and more secure, join us.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            <a
              href="mailto:hello@nirvik.ai"
              className="inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground px-7 py-3.5 rounded-xl text-base font-semibold transition-all hover:opacity-90 hover:-translate-y-0.5 shadow-[0_20px_40px_-15px_color-mix(in_oklch,var(--primary)_45%,transparent)]"
            >
              <Mail className="h-5 w-5" />
              Get in Touch
            </a>
            <a
              href="https://trinetra.nirvik.ai"
              className="inline-flex items-center justify-center gap-2 bg-secondary text-foreground px-7 py-3.5 rounded-xl text-base font-semibold border border-border hover:bg-accent transition-all"
            >
              Try Trinetra
              <ArrowRight className="h-5 w-5" />
            </a>
          </div>
        </div>
      </section>

      {/* ─── Tricolor strip ───
       * Saffron / white / india-green — a quiet heritage cue just above
       * the footer. Thin (2px) to read as an identity mark, not a banner. */}
      <div className="relative z-10 flex">
        <div className="h-0.5 flex-1 bg-saffron" />
        <div className="h-0.5 flex-1 bg-white" />
      </div>

      {/* ─── Footer ─── */}
      <footer className="relative z-10 px-5 sm:px-6 py-10 border-t border-border/50">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <TrinetraLoop3 className="h-5 w-5 text-primary-strong" />
            <span className="font-display text-sm font-semibold tracking-wide text-muted-foreground">Nirvik AI Labs</span>
            <span className="text-xs text-muted-foreground/70 ml-2">— Founded April 7, 2026</span>
          </div>
          <p className="text-xs text-muted-foreground text-center sm:text-right">
            A new era of cyber defense begins. &copy; 2026 Nirvik AI Labs.
          </p>
        </div>
      </footer>
    </div>
  );
}
